/**
 * Tasks: what is due, across every record.
 *
 * My tasks, Overdue, Today and This week are the signed-in person's own open
 * tasks; All is the team's, for someone who may see other people's (with
 * edit_activities, or a manager). "Today" and "this week" are UK days, worked
 * out by the server, so the tabs, the badge and a reminder all agree.
 *
 * Each task links to the records it is on and can be ticked from here.
 * Quick add makes a task with no record, which needs edit_activities;
 * `?new=1` (from ⌘K) puts the cursor in it.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, Pagination, Stack, Tabs, Text, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBriefcase, IconBuilding, IconPlus, IconUser } from '@tabler/icons-react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useCreateActivity, useTaskCounts, useTasks, type Activity, type ActivityLink, type TaskListParams,
} from '../../api/activities';
import { TOKENS } from '../../theme';
import { PageHeader } from '../../ui';
import { useVocab } from '../../vocab';
import { LeadDetailModal } from '../LeadDetailModal';
import { useCrmPermissions } from './crmShared';
import { dueText, exactTime, TaskCheck } from './Timeline';

const PAGE_SIZE = 50;

type TabKey = 'mine' | 'overdue' | 'today' | 'week' | 'team';
const TAB_PARAMS: Record<TabKey, TaskListParams> = {
  mine: { owner: 'me', due: 'all' },
  overdue: { owner: 'me', due: 'overdue' },
  today: { owner: 'me', due: 'today' },
  week: { owner: 'me', due: 'week' },
  team: { owner: 'all', due: 'all' },
};

export function TasksPage() {
  const v = useVocab();
  const perms = useCrmPermissions();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as TabKey | null;
  const seesTeam = perms.editActivities || ['manager', 'admin', 'super_admin'].includes(perms.user?.role ?? '');
  const tab: TabKey = raw && raw in TAB_PARAMS && (raw !== 'team' || seesTeam) ? raw : 'mine';
  const [page, setPage] = useState(1);
  const tasks = useTasks({ ...TAB_PARAMS[tab], page, page_size: PAGE_SIZE });
  const counts = useTaskCounts().data;
  const [leadId, setLeadId] = useState<string | null>(null);

  function setTab(t: string | null) {
    const next = new URLSearchParams(params);
    if (!t || t === 'mine') next.delete('tab');
    else next.set('tab', t);
    setParams(next, { replace: true });
    setPage(1);
  }

  const label = (text: string, n: number | undefined, red = false) => (
    <Group gap={6} wrap="nowrap">
      <span>{text}</span>
      {n !== undefined && n > 0 && (
        <Badge size="xs" variant={red ? 'filled' : 'light'} color={red ? 'red' : 'gray'} circle={n < 10}>{n}</Badge>
      )}
    </Group>
  );

  const list = tasks.data?.tasks ?? [];
  const pages = tasks.data?.pagination.total_pages ?? 1;

  return (
    <Stack gap="md">
      <PageHeader
        title={v.Many('task')}
        description={`Open ${v.many('task')} across every ${v.one('deal')}, ${v.one('company')} and ${v.one('lead')}. Days are UK days.`}
      />
      {perms.editActivities && <QuickAdd focus={params.get('new') === '1'} />}
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="mine">{label(`My ${v.many('task')}`, counts?.open)}</Tabs.Tab>
          <Tabs.Tab value="overdue">{label('Overdue', counts?.overdue, true)}</Tabs.Tab>
          <Tabs.Tab value="today">{label('Today', counts?.today)}</Tabs.Tab>
          <Tabs.Tab value="week">{label('This week', counts?.week)}</Tabs.Tab>
          {seesTeam && <Tabs.Tab value="team">All</Tabs.Tab>}
        </Tabs.List>
      </Tabs>
      {tasks.error && <Alert color="red" variant="light">{tasks.error.message}</Alert>}
      {tasks.isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {!tasks.isPending && list.length === 0 && (
        <Card p="lg">
          <Text ta="center" c="dimmed" size="sm">
            {tab === 'overdue' ? `Nothing overdue.` : `No open ${v.many('task')} here.`}
          </Text>
        </Card>
      )}
      {list.length > 0 && (
        <Card p={0}>
          <Stack gap={0}>
            {list.map((t, i) => (
              <TaskRow key={t.id} task={t} first={i === 0} showOwner={tab === 'team'} onLead={setLeadId} />
            ))}
          </Stack>
        </Card>
      )}
      {pages > 1 && <Pagination value={page} onChange={setPage} total={pages} size="sm" />}
      <LeadDetailModal id={leadId} onClose={() => setLeadId(null)} />
    </Stack>
  );
}

const LINK_STYLE = { display: 'inline-flex', alignItems: 'center', gap: 3, color: TOKENS.textSecondary } as const;

/** A record a task is on, with an icon for its kind, as a link to it. */
function RecordLink({ link, onLead }: { link: ActivityLink; onLead: (id: string) => void }) {
  const v = useVocab();
  switch (link.object_type) {
    case 'deal':
      return (
        <Anchor component={Link} to={`/deals?deal=${encodeURIComponent(link.object_id)}`} size="xs" underline="always" style={LINK_STYLE}>
          <IconBriefcase size={12} />{link.label ?? v.One('deal')}
        </Anchor>
      );
    case 'company':
      return (
        <Anchor component={Link} to={`/companies/${encodeURIComponent(link.object_id)}`} size="xs" underline="always" style={LINK_STYLE}>
          <IconBuilding size={12} />{link.label ?? v.One('company')}
        </Anchor>
      );
    case 'submission':
      return (
        <Anchor size="xs" underline="always" style={LINK_STYLE} onClick={() => onLead(link.object_id)}>
          <IconUser size={12} />{link.label ?? `${v.One('lead')} ${link.object_id.slice(0, 8)}`}
        </Anchor>
      );
  }
}

function TaskRow({
  task, first, showOwner, onLead,
}: { task: Activity; first: boolean; showOwner: boolean; onLead: (id: string) => void }) {
  return (
    <Group
      gap="sm"
      wrap="nowrap"
      align="flex-start"
      px="md"
      py="sm"
      data-testid="task-row"
      style={{ borderTop: first ? undefined : `1px solid ${TOKENS.border}` }}
    >
      <div style={{ paddingTop: 2 }}><TaskCheck activity={task} /></div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <Text
          size="sm"
          fw={500}
          style={{
            color: TOKENS.text,
            overflowWrap: 'anywhere',
            textDecoration: task.status === 'done' ? 'line-through' : undefined,
          }}
        >
          {task.subject}
        </Text>
        <Group gap={8} wrap="wrap" mt={2}>
          <Text
            size="xs"
            title={exactTime(task.due_at)}
            style={{ color: task.overdue ? TOKENS.redText : TOKENS.textMuted }}
            data-overdue={task.overdue || undefined}
          >
            {task.overdue ? `Overdue · ${dueText(task.due_at)}` : dueText(task.due_at)}
          </Text>
          {task.links.map((l) => (
            <RecordLink key={`${l.object_type}:${l.object_id}`} link={l} onLead={onLead} />
          ))}
          {showOwner && <Text size="xs" c="dimmed">{task.owner_name ?? 'Nobody'}</Text>}
        </Group>
      </div>
    </Group>
  );
}

/** Add a task with a subject and an optional due time, owned by me. */
function QuickAdd({ focus }: { focus: boolean }) {
  const v = useVocab();
  const create = useCreateActivity();
  const [subject, setSubject] = useState('');
  const [due, setDue] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (focus) input.current?.focus();
  }, [focus]);

  async function add() {
    if (!subject.trim()) return;
    try {
      await create.mutateAsync({
        kind: 'task',
        subject: subject.trim(),
        owner_user_id: 'me',
        ...(due ? { due_at: due } : {}),
        links: [],
      });
      notifications.show({ color: 'teal', title: `${v.One('task')} added`, message: subject.trim() });
      setSubject('');
      setDue('');
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not added', message: (e as Error).message });
    }
  }

  return (
    <Card p="sm">
      <Group gap="xs" wrap="wrap" align="flex-end">
        <TextInput
          ref={input}
          size="sm"
          label={`New ${v.one('task')}`}
          placeholder="What needs doing"
          value={subject}
          onChange={(e) => setSubject(e.currentTarget.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
          style={{ flex: '1 1 220px' }}
        />
        <TextInput
          size="sm"
          type="datetime-local"
          label="Due"
          value={due}
          onChange={(e) => setDue(e.currentTarget.value)}
          style={{ flex: '0 1 200px' }}
        />
        <Button size="sm" leftSection={<IconPlus size={14} />} onClick={add} loading={create.isPending} disabled={!subject.trim()}>
          Add
        </Button>
      </Group>
    </Card>
  );
}
